
import { useState, useEffect } from 'react';
import { CVEItem } from '@/types/cve';
import { useLanguage } from '../useLanguage';

export function useCVEFilter() {
  const { language } = useLanguage();

  // Initialize with both English and Polish severity values to ensure filters remain active when switching languages
  const [selectedSeverities, setSelectedSeverities] = useState<string[]>([
    'critical', 'high', 'medium', 'low',
    'krytyczny', 'wysoki', 'średni', 'niski'
  ]);

  // Filter CVE items based on selected severities
  const filterCVEItems = (items: CVEItem[]): CVEItem[] => {
    return items.filter(item => {
      // Check against both language versions of severities
      const severityEN = item.severity_en.toLowerCase();
      const severityPL = item.severity.toLowerCase();
      return selectedSeverities.includes(severityEN) || selectedSeverities.includes(severityPL);
    });
  };

  // Toggle severity filter - handles both language versions of the same severity
  const toggleSeverity = (severity: string) => {
    const severityToToggle = severity.toLowerCase();

    // Get matching severity in other language
    let otherLanguageSeverity = '';
    if (language === 'en') {
      // If toggling English, get Polish equivalent
      if (severityToToggle === 'critical') otherLanguageSeverity = 'krytyczny';
      else if (severityToToggle === 'high') otherLanguageSeverity = 'wysoki';
      else if (severityToToggle === 'medium') otherLanguageSeverity = 'średni';
      else if (severityToToggle === 'low') otherLanguageSeverity = 'niski';
    } else {
      // If toggling Polish, get English equivalent
      if (severityToToggle === 'krytyczny') otherLanguageSeverity = 'critical';
      else if (severityToToggle === 'wysoki') otherLanguageSeverity = 'high';
      else if (severityToToggle === 'średni') otherLanguageSeverity = 'medium';
      else if (severityToToggle === 'niski') otherLanguageSeverity = 'low';
    }

    // Check if this severity is currently the ONLY one selected
    // We check if selectedSeverities has exactly 2 items (the pair) AND includes our target
    const isExclusivelySelected =
      selectedSeverities.length === 2 &&
      selectedSeverities.includes(severityToToggle);

    if (isExclusivelySelected) {
      // If already exclusively selected, reset to ALL severities
      setSelectedSeverities([
        'critical', 'high', 'medium', 'low',
        'krytyczny', 'wysoki', 'średni', 'niski'
      ]);
    } else {
      // Otherwise, select ONLY this severity (and its translation)
      setSelectedSeverities([severityToToggle, otherLanguageSeverity]);
    }
  };

  return {
    selectedSeverities,
    toggleSeverity,
    filterCVEItems
  };
}
